// ============================================================================
// PDF deeplinks for a PO's documents — the purchase order and the delivery
// challan, streamed live from Zoho by the backend (so no Zoho login is needed,
// unlike the Zoho column's web-app links). Opens in a new tab.
//
// Shared by the three surfaces that offer them, because the buttons must look
// and behave the same wherever an operator reaches for the DC: the order
// detail's stage table (per LEG, off `stage.zoho_docs`), the OMS PO list and the
// board's contents modal (per PO, off the allotment's `documents`). Only docs
// the server gave a `pdf_url` are rendered — an unpushed doc has no PDF, and a
// button that 404s is worse than no button.
// ============================================================================
import { FilePdfOutlined } from "@ant-design/icons";
import { Button, Space, Tooltip } from "antd";
import React from "react";
import { DocPdfRef } from "../types";

const DOC_PDF_LABEL: Record<string, string> = {
  purchase_order: "PO",
  delivery_challan: "DC",
  invoice: "Invoice",
};

const label = (docType: string) => DOC_PDF_LABEL[docType] || docType.replace(/_/g, " ");

export const hasDocPdfs = (docs: DocPdfRef[] | null | undefined) =>
  !!docs?.some((d) => d.pdf_url);

const DocPdfButtons: React.FC<{
  docs: DocPdfRef[] | null | undefined;
  size?: "small" | "middle";
}> = ({ docs, size = "small" }) => {
  const withPdf = (docs ?? []).filter((d) => d.pdf_url);
  if (!withPdf.length) return null;
  return (
    <Space size={4} wrap>
      {withPdf.map((d, i) => (
        <Tooltip
          // The number is the useful part of the hover: a truck's manifest can carry two challans
          // for one PO, and the operator has to know WHICH one they are about to open.
          key={`${d.doc_type}-${d.number || i}`}
          title={
            d.number
              ? `Open ${d.number} — the ${d.doc_type.replace(/_/g, " ")} PDF`
              : `Open the ${d.doc_type.replace(/_/g, " ")} PDF`
          }
        >
          <Button
            size={size}
            icon={<FilePdfOutlined />}
            style={{ color: "#cf1322", borderColor: "#ffccc7" }}
            href={d.pdf_url as string}
            target="_blank"
            rel="noopener noreferrer"
            // These sit inside clickable rows (the PO list row opens the order) — a click on the
            // button must open the PDF only.
            onClick={(e) => e.stopPropagation()}
          >
            {label(d.doc_type)}
          </Button>
        </Tooltip>
      ))}
    </Space>
  );
};

export default DocPdfButtons;
